import { useRef } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { Check, ArrowRight, Phone } from "lucide-react";
import PageLayout from "@/components/site/PageLayout";
import PageHeader from "@/components/site/PageHeader";
import Contact from "@/components/site/Contact";
import SEO from "@/components/site/SEO";
import { Reveal } from "@/components/site/motion";
import { serviceBySlug, SERVICES } from "@/lib/services";
import { BUSINESS } from "@/lib/api";
import { trackConversion } from "@/lib/gtag";

export default function ServiceDetailPage() {
  const { slug } = useParams();
  const nav = useNavigate();
  const service = serviceBySlug(slug);
  const contactRef = useRef(null);

  if (!service) {
    return (
      <PageLayout testid="service-not-found">
        <div className="pt-[180px] pb-20 text-center">
          <h1 className="font-display font-black text-3xl text-steel-900">Service not found</h1>
          <button onClick={() => nav("/services")} className="mt-6 bg-steel-900 text-white px-6 py-3">Back to services</button>
        </div>
      </PageLayout>
    );
  }

  const jsonLd = {
    "@context": "https://schema.org",
    "@type": "Service",
    name: service.name,
    serviceType: service.name,
    areaServed: { "@type": "Place", name: "Hyderabad, Telangana, India" },
    provider: {
      "@type": "LocalBusiness",
      name: BUSINESS.name,
      telephone: `+91${BUSINESS.phone}`,
      email: BUSINESS.email,
    },
    description: service.metaDesc,
  };

  return (
    <PageLayout testid={`service-${service.slug}`}>
      <SEO title={service.metaTitle} description={service.metaDesc} path={`/services/${service.slug}`} jsonLd={jsonLd} />
      <PageHeader
        eyebrow="Specialised service"
        title={service.headline}
        crumb={service.name}
        subtitle={service.intro}
      />

      {/* Bullets */}
      <section className="py-20 md:py-24 bg-white">
        <div className="max-w-[1200px] mx-auto px-5 md:px-10 grid lg:grid-cols-12 gap-10">
          <Reveal className="lg:col-span-5">
            <p className="font-label text-xs tracking-[0.3em] uppercase text-steel-500 mb-4">What's included</p>
            <h2 className="font-display font-black tracking-tighter text-steel-900 text-3xl md:text-4xl leading-none">Built for safety, built for Hyderabad.</h2>
          </Reveal>
          <ul className="lg:col-span-7 grid sm:grid-cols-2 gap-4">
            {service.bullets.map((b, i) => (
              <Reveal key={i} delay={i * 0.05}>
                <li className="flex items-start gap-3 border border-steel-200 p-5 text-steel-700">
                  <Check className="text-gold-500 mt-0.5 shrink-0" size={18} />
                  <span>{b}</span>
                </li>
              </Reveal>
            ))}
          </ul>
        </div>
      </section>

      {/* Process */}
      <section className="py-20 md:py-24 bg-steel-50 border-y border-steel-200">
        <div className="max-w-[1200px] mx-auto px-5 md:px-10">
          <Reveal className="mb-10">
            <p className="font-label text-xs tracking-[0.3em] uppercase text-steel-500 mb-4">Installation process</p>
            <h2 className="font-display font-black tracking-tighter text-steel-900 text-3xl md:text-4xl leading-none">How we install.</h2>
          </Reveal>
          <ol className="grid md:grid-cols-2 lg:grid-cols-4 gap-5">
            {service.process.map(([title, body], i) => (
              <Reveal key={title} delay={i * 0.06}>
                <li className="border border-steel-200 bg-white p-6 h-full">
                  <div className="font-display font-black text-3xl text-gold-500">0{i + 1}</div>
                  <h3 className="font-display font-bold text-lg text-steel-900 mt-3 mb-2">{title}</h3>
                  <p className="text-steel-600 text-sm leading-relaxed">{body}</p>
                </li>
              </Reveal>
            ))}
          </ol>
        </div>
      </section>

      {/* Mini FAQ */}
      <section className="py-20 md:py-24 bg-white">
        <div className="max-w-[900px] mx-auto px-5 md:px-10">
          <Reveal className="mb-8">
            <p className="font-label text-xs tracking-[0.3em] uppercase text-steel-500 mb-4">Quick FAQ</p>
            <h2 className="font-display font-black tracking-tighter text-steel-900 text-3xl md:text-4xl leading-none">You might ask</h2>
          </Reveal>
          {service.faqs.map(([q, a]) => (
            <Reveal key={q}>
              <details className="border-b border-steel-200 py-5 group">
                <summary className="font-display font-bold text-lg text-steel-900 cursor-pointer list-none flex justify-between items-center">
                  {q}
                  <span className="text-gold-500 text-2xl group-open:rotate-45 transition-transform">+</span>
                </summary>
                <p className="text-steel-600 mt-3 leading-relaxed">{a}</p>
              </details>
            </Reveal>
          ))}
          <div className="mt-10 flex flex-col sm:flex-row gap-4">
            <Link to="/contact" className="group bg-steel-900 text-white px-8 py-4 font-semibold flex items-center justify-center gap-3 hover:bg-steel-800 transition-colors">
              Request Free Quote <ArrowRight size={18} className="group-hover:translate-x-1 transition-transform" />
            </Link>
            <a href={`tel:+91${BUSINESS.phone}`} onClick={trackConversion} className="border border-steel-300 text-steel-900 px-8 py-4 font-semibold flex items-center justify-center gap-3 hover:border-gold-400 hover:text-gold-600 transition-colors">
              <Phone size={18} /> Call Now
            </a>
          </div>
          <div className="mt-10 pt-10 border-t border-steel-200">
            <p className="font-label text-xs uppercase tracking-widest text-steel-500 mb-4">Explore more</p>
            <div className="flex flex-wrap gap-3">
              {SERVICES.filter((s) => s.slug !== service.slug).map((s) => (
                <Link key={s.slug} to={`/services/${s.slug}`} className="border border-steel-300 px-5 py-2.5 text-sm font-label text-steel-800 hover:bg-steel-900 hover:text-white hover:border-steel-900 transition-colors">
                  {s.name} →
                </Link>
              ))}
              <Link to="/projects" className="border border-steel-300 px-5 py-2.5 text-sm font-label text-steel-800 hover:bg-steel-900 hover:text-white hover:border-steel-900 transition-colors">Our Projects →</Link>
            </div>
          </div>
        </div>
      </section>

      <Contact ref={contactRef} />
    </PageLayout>
  );
}
